// ui/web/src/providers/ShareEventsProvider.tsx
import { useState, useEffect } from "react"
import { useAuthStore } from "@/shared/stores/authStore"
import { useQueryClient } from "@tanstack/react-query"
import { AccessRevokedModal } from "@/features/personal/components/AccessRevokedModal"
import type { ShareEvent } from "@/shared/hooks/useShareEvents"

interface ShareEventsProviderProps {
  children: React.ReactNode
}

export function ShareEventsProvider({ children }: ShareEventsProviderProps) {
  const [revokeEvent, setRevokeEvent] = useState<ShareEvent | null>(null)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const qc = useQueryClient()

  useEffect(() => {
    if (!isAuthenticated) return

    const es = new EventSource("/api/v1/me/events")

    es.addEventListener("share.revoked", (e: MessageEvent) => {
      const evt = JSON.parse(e.data) as ShareEvent
      qc.invalidateQueries({ queryKey: ["shared-with-me"] })
      qc.invalidateQueries({ queryKey: ["my-space"] })
      setRevokeEvent(evt)
    })

    es.addEventListener("share.granted", (e: MessageEvent) => {
      const evt = JSON.parse(e.data) as ShareEvent
      // Trigger notification badge via custom event
      window.dispatchEvent(new CustomEvent("notification:share_granted", { detail: evt }))
    })

    return () => es.close()
  }, [isAuthenticated, qc])

  return (
    <>
      {children}
      {revokeEvent && (
        <AccessRevokedModal
          event={revokeEvent}
          onClose={() => setRevokeEvent(null)}
        />
      )}
    </>
  )
}
